import { Button, useMediaQuery } from '@mui/material';
import { useSubmission } from 'api/submission';
import chatIcon from 'assets/icons/chat.svg';
import { SvgIconWrapper } from 'components/SvgIconWrapper';
import { useFrontChat } from 'front-chat-sdk/hooks';
import theme from 'providers/theme/theme';
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useChatWidget } from 'store/features/chatWidget';
import { FontChatButtonStyle } from './FrontChat.styles';

const FrontChat = () => {
  const { id } = useParams();
  const { data: submission, isLoading: submissionLoading } = useSubmission(String(id));
  const isDesktopLayout = useMediaQuery(theme.breakpoints.up('md'));

  const CHAT_ID = import.meta.env.REACT_APP_CONSUMER_FRONT_CHAT_ID;

  const frontChatData = useMemo(
    () => ({
      email: submission?.email || '',
      name: `${submission?.data?.uw_questions?.contactFirst || ''} ${submission?.data?.uw_questions?.contactLast || ''}`.trim(),
      quoteNumber: submission?.quote_number || '',
      userHash: submission?.front_chat_hash || '',
      userId: id || '', // using submissionId as userId
    }),
    [
      submission?.email,
      submission?.data?.uw_questions?.contactFirst,
      submission?.data?.uw_questions?.contactLast,
      submission?.front_chat_hash,
      submission?.quote_number,
    ],
  );

  const { isChatWidgetWindowVisible, setWindowVisibility, setIsInitialized } = useChatWidget();

  const { initialize, frontChat, isInitialized } = useFrontChat();

  const chatCanLoad = useMemo(
    () => Boolean(CHAT_ID && !submissionLoading && submission?.quote_number),
    [CHAT_ID, submission?.quote_number, submissionLoading],
  );

  useEffect(
    () =>
      // cleanup needs to be in a separate effect handler, was prematurely shutting down the chat
      () => {
        if (isInitialized) {
          frontChat?.('shutdown');
        }
      },
    [isInitialized],
  );

  useEffect(() => {
    if (chatCanLoad) {
      const { email, name, quoteNumber, userHash, userId } = frontChatData;

      initialize?.({
        // onInitCompleted gets overridden by the SDK itself, so we can't rely on it here
        chatId: CHAT_ID,
        useDefaultLauncher: false,
        shouldExpandOnShowWindow: false,
        // verified identity if received frontChat hash from BE
        ...(userHash && {
          userHash,
          userId,
        }),
        name,
        contact: {
          email,
        },
        customFields: {
          quoteNumber,
        },
      });
    } else if (isInitialized) {
      // if we don't have a valid frontChat data, shut down the chat widget
      frontChat?.('shutdown', { clearSession: true });
    }
  }, [chatCanLoad, initialize, isInitialized, frontChatData]);

  useEffect(() => {
    let unbind: ReturnType<NonNullable<typeof frontChat>> | undefined;

    if (isInitialized) {
      setIsInitialized();
      unbind = frontChat?.('onWindowVisibilityChanged', ({ is_window_visible }) =>
        setWindowVisibility(is_window_visible),
      );
    }

    return () => {
      unbind?.();
    };
  }, [frontChat, isInitialized]);

  const onClick = () => {
    if (!isInitialized) {
      console.warn('[FrontChat] Cannot show chat: Not initialized');
    } else if (typeof frontChat !== 'function') {
      console.warn('[FrontChat] Cannot show chat: frontChat unavailable');
    } else {
      frontChat('show');
    }
  };

  const displayButton = isDesktopLayout && isInitialized && !isChatWidgetWindowVisible;

  return (
    displayButton && (
      <Button onClick={onClick} sx={FontChatButtonStyle}>
        <SvgIconWrapper src={chatIcon} />
      </Button>
    )
  );
};

export default FrontChat;
